import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Mail,
  MapPin,
  Send,
  CheckCircle2,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SERVICES = [
  "SAP Implementation & Migration",
  "SAP S/4HANA Enablement",
  "RISE with SAP & Public Cloud",
  "Business Analytics & Reporting",
  "Application Management Services",
  "SAP Upgrades & Enhancements",
  "Not sure yet",
];

const initialForm = {
  name: "",
  email: "",
  company: "",
  service: "",
  message: "",
};

const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email, and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSubmitted(true);
      setForm(initialForm);
      toast.success("Thanks for submitting! Our team will get back to you within one business day.");
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again or email anila@anilaconsulting.com.";
      toast.error(typeof detail === "string" ? detail : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* HERO */}
      <section
        data-testid="contact-hero"
        className="bg-[#F5F5DC] pt-32 pb-16 lg:pt-40 lg:pb-20"
      >
        <div className="container-pro max-w-4xl">
          <p className="overline mb-6 fade-up">Contact Us</p>
          <h1 className="font-display text-5xl lg:text-7xl text-[#111111] leading-[1.05] fade-up-delay-1">
            Let's <span className="text-[#8B0000]">chat.</span>
          </h1>
          <p className="text-lg text-[#4A4A4A] leading-relaxed mt-8 max-w-2xl fade-up-delay-2">
            Tell us about your SAP landscape and the question on your desk.
            A senior consultant from Anila Consulting will respond personally
            within one business day.
          </p>
        </div>
      </section>

      {/* FORM + CONTACT INFO */}
      <section
        data-testid="contact-form-section"
        className="bg-[#F5F5DC] pb-24 lg:pb-32"
      >
        <div className="container-pro grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* FORM */}
          <div className="lg:col-span-7">
            {submitted ? (
              <div
                data-testid="contact-success"
                className="bg-white p-10 lg:p-14 border border-[#E5E5E5] flex flex-col items-start"
              >
                <CheckCircle2
                  size={42}
                  strokeWidth={1.5}
                  className="text-[#8B0000] mb-6"
                />
                <h3 className="font-display text-3xl text-[#111111] mb-4">
                  Thanks for submitting!
                </h3>
                <p className="text-[#4A4A4A] leading-relaxed">
                  Your message has reached our team at Anila Consulting. We
                  will review your request and respond personally within one
                  business day. For urgent matters, please email{" "}
                  <a
                    href="mailto:anila@anilaconsulting.com"
                    className="text-[#8B0000] underline-offset-2 hover:underline"
                  >
                    anila@anilaconsulting.com
                  </a>{" "}
                  directly.
                </p>
                <button
                  data-testid="contact-reset-button"
                  onClick={() => setSubmitted(false)}
                  className="btn-secondary mt-8"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form
                data-testid="contact-form"
                onSubmit={handleSubmit}
                className="bg-white p-8 lg:p-12 border border-[#E5E5E5] flex flex-col gap-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field
                    label="First name"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="First name"
                    required
                    testId="contact-input-name"
                  />
                  <Field
                    label="Email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="you@company.com"
                    required
                    testId="contact-input-email"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field
                    label="Company"
                    name="company"
                    value={form.company}
                    onChange={handleChange}
                    placeholder="Company name"
                    testId="contact-input-company"
                  />
                  <div className="flex flex-col gap-2">
                    <label className="text-xs uppercase tracking-[0.2em] font-medium text-[#111111]">
                      Area of interest
                    </label>
                    <select
                      data-testid="contact-input-service"
                      name="service"
                      value={form.service}
                      onChange={handleChange}
                      className="border border-[#E5E5E5] px-4 py-3 bg-white text-[#111111] focus:border-[#8B0000] transition-colors"
                    >
                      <option value="">Select a service</option>
                      {SERVICES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-xs uppercase tracking-[0.2em] font-medium text-[#111111]">
                    Message <span className="text-[#8B0000]">*</span>
                  </label>
                  <textarea
                    data-testid="contact-input-message"
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={6}
                    required
                    placeholder="Briefly describe your SAP landscape and what you're looking for."
                    className="border border-[#E5E5E5] px-4 py-3 bg-white text-[#111111] resize-none focus:border-[#8B0000] transition-colors"
                  />
                </div>

                <button
                  data-testid="contact-submit-button"
                  type="submit"
                  disabled={submitting}
                  className="btn-primary self-start disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "Sending..." : "Send"}
                  {!submitting && <Send size={16} />}
                </button>
                <p className="text-xs text-[#4A4A4A] leading-relaxed">
                  By sending this message you agree to be contacted by Anila
                  Consulting regarding your inquiry. We respect confidentiality
                  on all engagements.
                </p>
              </form>
            )}
          </div>

          {/* CONTACT INFO */}
          <aside className="lg:col-span-5 flex flex-col gap-8">
            <div className="bg-[#111111] text-white p-10">
              <p className="overline mb-5">Direct contact</p>
              <h3 className="font-display text-3xl text-white leading-snug mb-6">
                Speak with our team.
              </h3>
              <ul className="space-y-5 text-white/80">
                <li className="flex items-start gap-4">
                  <Mail size={20} className="text-[#8B0000] mt-1" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-white/60 mb-1">
                      Email us at
                    </p>
                    <a
                      data-testid="contact-email-link"
                      href="mailto:anila@anilaconsulting.com"
                      className="hover:text-white transition-colors break-all"
                    >
                      anila@anilaconsulting.com
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4">
                  <MapPin size={20} className="text-[#8B0000] mt-1" />
                  <div>
                    <p className="text-xs uppercase tracking-[0.2em] text-white/60 mb-1">
                      Branches
                    </p>
                    <p>Gurugram · Mumbai · Bangalore</p>
                    <Link
                      to="/branches"
                      data-testid="contact-branches-link"
                      className="inline-block mt-2 text-[#8B0000] text-sm uppercase tracking-wider hover:text-white transition-colors"
                    >
                      View all branches →
                    </Link>
                  </div>
                </li>
              </ul>
            </div>

            <div className="bg-white p-10 border border-[#E5E5E5]">
              <p className="overline mb-4">Why reach out</p>
              <h4 className="font-display text-2xl text-[#111111] mb-3">
                Get a tailored quote.
              </h4>
              <p className="text-[#4A4A4A] leading-relaxed">
                Our expertise ensures expert support on all modules of the SAP
                landscape — implementation, migration, analytics, AMS and
                more. Tell us what you need and we'll come back with a clear,
                costed plan.
              </p>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, testId, required, ...props }) => (
  <div className="flex flex-col gap-2">
    <label className="text-xs uppercase tracking-[0.2em] font-medium text-[#111111]">
      {label} {required && <span className="text-[#8B0000]">*</span>}
    </label>
    <input
      data-testid={testId}
      required={required}
      {...props}
      className="border border-[#E5E5E5] px-4 py-3 bg-white text-[#111111] focus:border-[#8B0000] transition-colors"
    />
  </div>
);

export default Contact;
